import React, { useState, useEffect, useMemo, useRef } from 'react';
import { api } from '../utils/api';
import HelpTooltip, { HELP } from '../components/HelpTooltip';
import { Save, CheckCircle, AlertTriangle } from 'lucide-react';
import { PeerFeedbackComposer } from '../components/PeerFeedback';
import { acqDenominator, invalidSeatCount, sumValidSeat } from '../utils/seat-rate.mjs';

const card = { background: 'var(--field)', border: '1px solid var(--rule-strong)', boxShadow: 'none', borderRadius: 18, padding: '20px', marginBottom: 16 };
const label = { fontSize: 'var(--t-note)', fontWeight: 600, color: 'var(--ink-soft)', marginBottom: 6, display: 'block' };
const inp   = { width: '100%', padding: '10px 14px', background: 'var(--paper-sunk)', border: '1px solid var(--rule-strong)', borderRadius: 10, color: 'var(--ink)', fontSize: 14, boxSizing: 'border-box', outline: 'none', colorScheme: 'dark' };

const presetBtn = (active) => ({
  flex: 1, padding: '10px 8px', borderRadius: 10,
  fontSize: 'var(--t-note)', fontWeight: 700,
  border: `1.5px solid ${active ? 'var(--mark)' : 'var(--ink-mute)'}`,
  background: active ? 'var(--mark-soft)' : 'transparent',
  color: active ? 'var(--mark)' : 'var(--ink-soft)',
  cursor: 'pointer',
  minHeight: 44,
});

function Section({ title, children }) {
  return (
    <div style={card}>
      <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--ink-soft)', marginBottom: 16 }}>{title}</div>
      {children}
    </div>
  );
}

// カウント(キャッチ〜獲得)は時間別入力が入力元。日報では編集せず自動集計を表示するだけ。
const blankForm = (date) => ({
  reflection_date: date,
  top_lost_reason: '', why_text: '', prevent_stage: '',
  improvement_text: '', next_pitch_text: '', tomorrow_theme: '',
  booth_cost: '',
  eval_entry: 0, eval_flow: 0, eval_close: 0,
});

const RATIOS = [
  { label: 'ティッシュ率',     num: 'sticker',   den: 'catch' },
  { label: 'トーク率',     num: 'talk',      den: 'sticker' },
  { label: 'フルトーク率', num: 'full_talk', den: 'talk' },
  { label: '着座率',       num: 'seat',      den: 'full_talk' },
  { label: '獲得率',       num: 'acq',       den: 'valid_seat' }, // 分母は有効着座（ADR 0028）
  { label: 'キャッチ→獲得', num: 'acq',      den: 'catch' },
];
const SUMMARY_METRICS = [
  { key: 'catch',     label: 'キャッチ',   color: 'var(--mark)' },
  { key: 'sticker',   label: 'ティッシュ',     color: 'var(--mark)' },
  { key: 'talk',      label: 'トーク',     color: 'var(--mark)' },
  { key: 'full_talk', label: 'フルトーク', color: 'var(--mark)' },
  { key: 'seat',      label: '着座',       color: 'var(--mark)' },
  { key: 'acq',       label: '獲得',       color: 'var(--gain)' },
];
const pct = (a, b) => (b ? ((a / b) * 100).toFixed(1) + '%' : '-');

const todayJst = () => new Date(Date.now() + 9 * 3600000).toISOString().slice(0, 10);
// 'YYYY-MM-DD' に日数を加減（UTC基準で計算し日付ズレを防ぐ）
const addDays = (ymd, n) => { const d = new Date(ymd + 'T00:00:00Z'); d.setUTCDate(d.getUTCDate() + n); return d.toISOString().slice(0, 10); };

// 書きかけ下書きの localStorage 永続化（画面遷移・リロードでの入力消失を防ぐ）。
// ユーザーごと・日付ごとにキーを分け、保存成功時に消す。
const DRAFT_PREFIX = 'shiftend_draft_v1_';
const draftKey = (uid, date) => `${DRAFT_PREFIX}${uid ?? 'me'}_${date}`;
const loadDraft = (uid, date) => {
  try { const s = localStorage.getItem(draftKey(uid, date)); return s ? JSON.parse(s) : null; }
  catch { return null; }
};
const saveDraft = (uid, date, form) => {
  try { localStorage.setItem(draftKey(uid, date), JSON.stringify(form)); } catch {}
};
const clearDraft = (uid, date) => {
  try { localStorage.removeItem(draftKey(uid, date)); } catch {}
};

export default function ShiftEnd({ user }) {
  const uid = user?.id;
  const [today, setToday] = useState(todayJst());
  const [selectedDate, setSelectedDate] = useState(today);
  const [form, setForm] = useState(blankForm(today));
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [loadingDate, setLoadingDate] = useState(false);
  const [existed, setExisted] = useState(false);
  const [dayMemos, setDayMemos] = useState([]);
  const [summary, setSummary] = useState(null); // 時間別入力から自動集計した本人の当日合計
  const [hourMemos, setHourMemos] = useState([]); // その日の時間別メモ（振り返りを書く材料）
  const [boothName, setBoothName] = useState(''); // その日のブース名（時間別入力で設定・読み取り専用）
  const [rolledOver, setRolledOver] = useState(false); // 入力中に日付をまたいだ告知
  // 転換率は既定で畳む。この画面の仕事は「振り返りを書くこと」で、率はその参考に過ぎない。
  const [showSummaryRatios, setShowSummaryRatios] = useState(false);
  const [restoredDraft, setRestoredDraft] = useState(false); // 書きかけ下書きを復元した告知
  const [boothCostError, setBoothCostError] = useState(false); // ブース代未入力エラー
  const boothCostRef = useRef(null);

  // 日付またぎ対策：PWAを開いたまま/復帰で日付が変わったら today を更新し、
  // 「今日」を表示中なら selectedDate を新しい今日へ追従（前日の日付のまま日報を上書きするのを防ぐ）。
  // ただし入力中（editedRef）は勝手に切り替えず、入力消失を防いで告知だけ出す。
  const todayRef = useRef(today);
  const selectedDateRef = useRef(selectedDate);
  const editedRef = useRef(false);
  useEffect(() => { selectedDateRef.current = selectedDate; }, [selectedDate]);
  useEffect(() => {
    const check = () => {
      const t = todayJst();
      if (t === todayRef.current) return;
      const prev = todayRef.current;
      todayRef.current = t;
      setToday(t);
      // 「今日」を見ていたときだけ追従を検討（過去日を意図的に見ているならそのまま）。
      if (selectedDateRef.current === prev) {
        if (editedRef.current) {
          setRolledOver(true); // 入力中：切り替えず、この日付で保存される旨を告知
        } else {
          setSelectedDate(t);  // 未入力：新しい今日へ追従（従来の前日固定バグ対策を維持）
        }
      }
    };
    const onVis = () => { if (document.visibilityState === 'visible') check(); };
    const id = setInterval(check, 30000);
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('focus', check);
    return () => { clearInterval(id); document.removeEventListener('visibilitychange', onVis); window.removeEventListener('focus', check); };
  }, []);

  // 日付変更時：着座メモを取得
  useEffect(() => {
    setDayMemos([]);
    api.getSeatMemos({ date_from: selectedDate, date_to: selectedDate })
      .then(setDayMemos).catch(() => setDayMemos([]));
  }, [selectedDate]);

  // 日付変更時：時間別入力から本人の当日合計を集計（読み取り専用サマリー）
  useEffect(() => {
    setSummary(null);
    setBoothName('');
    setHourMemos([]);
    api.getHourly(selectedDate).then(res => {
      setBoothName(res.booth_name || '');
      const mine = res.mine || {};
      const sum = { catch: 0, sticker: 0, talk: 0, full_talk: 0, seat: 0, acq: 0 };
      Object.values(mine).forEach(h => {
        sum.catch += h.catch || 0; sum.sticker += h.sticker || 0;
        sum.talk += h.talk || 0; sum.full_talk += h.full_talk || 0;
        sum.seat += h.seat || 0; sum.acq += h.acq || 0;
      });
      // 獲得率の分母。時間帯ごとの有効着座（サーバが返す）を合計する（ADR 0028）
      sum.valid_seat = sumValidSeat(Object.values(mine));
      setSummary(sum);
      // メモがある時間だけを時刻順に拾う
      setHourMemos(
        Object.entries(mine)
          .filter(([, h]) => (h.memo || '').trim())
          .map(([hour, h]) => ({ hour: Number(hour), memo: h.memo }))
          .sort((a, b) => a.hour - b.hour)
      );
    }).catch(() => { setSummary(null); setHourMemos([]); });
  }, [selectedDate]);

  useEffect(() => {
    setLoadingDate(true);
    setForm(blankForm(selectedDate));
    setExisted(false);
    editedRef.current = false; // 新しい日付を読み込んだので「入力中」フラグをリセット
    setRolledOver(false);
    setRestoredDraft(false);
    api.getReflectionByDate(selectedDate).then(r => {
      let base = blankForm(selectedDate);
      if (r && r.id) {
        base = {
          reflection_date: selectedDate,
          top_lost_reason: r.top_lost_reason || '', why_text: r.why_text || '',
          prevent_stage: r.prevent_stage || '', improvement_text: r.improvement_text || '',
          next_pitch_text: r.next_pitch_text || '', tomorrow_theme: r.tomorrow_theme || '',
          booth_cost: r.booth_cost ?? '',
          eval_entry: r.eval_entry || 0, eval_flow: r.eval_flow || 0, eval_close: r.eval_close || 0,
        };
        setExisted(true);
      }
      // 書きかけの下書きがあれば優先して復元（画面遷移・リロードで消えた入力を戻す）
      const draft = loadDraft(uid, selectedDate);
      if (draft) {
        setForm({ ...base, ...draft, reflection_date: selectedDate });
        editedRef.current = true;
        setRestoredDraft(true);
      } else {
        setForm(base);
      }
    }).catch(() => {}).finally(() => setLoadingDate(false));
  }, [selectedDate, uid]);

  const set = (k, v) => {
    editedRef.current = true;
    setForm(f => {
      const next = { ...f, [k]: v };
      saveDraft(uid, selectedDateRef.current, next); // 1文字ごとに下書き保存
      return next;
    });
  };

  // 復元した下書きを破棄してサーバー保存内容（無ければ空欄）に戻す
  const discardDraft = () => {
    clearDraft(uid, selectedDate);
    setRestoredDraft(false);
    editedRef.current = false;
    setLoadingDate(true);
    api.getReflectionByDate(selectedDate).then(r => {
      if (r && r.id) {
        setForm({
          reflection_date: selectedDate,
          top_lost_reason: r.top_lost_reason || '', why_text: r.why_text || '',
          prevent_stage: r.prevent_stage || '', improvement_text: r.improvement_text || '',
          next_pitch_text: r.next_pitch_text || '', tomorrow_theme: r.tomorrow_theme || '',
          booth_cost: r.booth_cost ?? '',
          eval_entry: r.eval_entry || 0, eval_flow: r.eval_flow || 0, eval_close: r.eval_close || 0,
        });
      } else {
        setForm(blankForm(selectedDate));
      }
    }).catch(() => {}).finally(() => setLoadingDate(false));
  };

  // 当日の着座メモ集計
  const dayStats = useMemo(() => {
    const total = dayMemos.length;
    const acq   = dayMemos.filter(m => m.result === '獲得').length;
    const lost  = dayMemos.filter(m => m.result === '失注').length;
    const followup = dayMemos.filter(m => m.result === '後追い').length;
    const lostMap = {};
    dayMemos.forEach(m => {
      if (m.result !== '獲得' && m.lost_reason) {
        lostMap[m.lost_reason] = (lostMap[m.lost_reason] || 0) + 1;
      }
    });
    const lostRanking = Object.entries(lostMap)
      .map(([reason, cnt]) => ({ reason, cnt }))
      .sort((a, b) => b.cnt - a.cnt);
    return { total, acq, lost, followup, lostRanking };
  }, [dayMemos]);

  const handleSave = async () => {
    // ブース代は必須。未入力なら保存せず該当欄へ誘導。
    if (String(form.booth_cost ?? '').trim() === '') {
      setBoothCostError(true);
      boothCostRef.current?.focus();
      boothCostRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    setSaving(true);
    try {
      await api.saveReflection({ ...form, reflection_date: selectedDate });
      clearDraft(uid, selectedDate); // 保存できたので下書きは不要
      setSaved(true);
      setExisted(true);
      editedRef.current = false;
      setRolledOver(false);
      setRestoredDraft(false);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) { alert(e.message); }
    setSaving(false);
  };

  // 日付プリセット（today=JSTを基準に加減）
  const setRelativeDate = (offsetDays) => setSelectedDate(addDays(today, offsetDays));

  const isToday = selectedDate === today;
  const dateLabel = isToday ? '今日' : (() => {
    const d = new Date(selectedDate);
    const diff = Math.round((d - new Date(today)) / 86400000);
    if (diff === -1) return '昨日';
    if (diff === -2) return '一昨日';
    if (diff < 0) return `${Math.abs(diff)}日前`;
    if (diff > 0) return `${diff}日後`;
    return '';
  })();

  return (
    <div style={{ maxWidth: 720, margin: '0 auto', padding: 'var(--s6) var(--s4) 120px' }}>
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 'var(--t-note)', color: 'var(--ink-mute)' }}>稼働日</div>
        <h1 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: 'var(--ink)' }}>日報（稼働終了時に入力）</h1>
        <div style={{ fontSize: 'var(--t-note)', color: 'var(--ink-soft)', marginTop: 4 }}>時間別入力の自動集計を確認し、振り返りを記録する</div>
      </div>

      {/* 日付選択 */}
      <div style={{ ...card, padding: 16, marginBottom: 16 }}>
        <label style={{ ...label, marginBottom: 10, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>入力する日付</span>
          {dateLabel && (
            <span style={{
              fontSize: 'var(--t-note)', fontWeight: 700,
              padding: '3px 10px', borderRadius: 8,
              background: isToday ? 'var(--mark-soft)' : 'var(--hold-bg)',
              color: isToday ? 'var(--mark)' : 'var(--hold)',
            }}>{dateLabel}</span>
          )}
        </label>
        <input
          type="date"
          value={selectedDate}
          max={today}
          onChange={e => setSelectedDate(e.target.value)}
          style={{ ...inp, fontSize: 16, padding: '12px 14px', minHeight: 48 }}
        />
        <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
          <button onClick={() => setRelativeDate(-1)} style={presetBtn(selectedDate === addDays(today, -1))}>昨日</button>
          <button onClick={() => setRelativeDate(0)} style={presetBtn(isToday)}>今日</button>
          <button onClick={() => setRelativeDate(-2)} style={presetBtn(selectedDate === addDays(today, -2))}>一昨日</button>
        </div>
        {rolledOver && (
          <div style={{
            marginTop: 10, padding: '10px 12px',
            background: 'var(--hold-bg)',
            border: '1px solid #5C4718',
            borderRadius: 8, fontSize: 'var(--t-note)', color: 'var(--hold)', fontWeight: 600, lineHeight: 1.5,
            display: 'flex', alignItems: 'flex-start', gap: 6,
          }}>
            <AlertTriangle size={15} style={{ flexShrink: 0, marginTop: 1 }} />
            <span>日付が変わりました。この日報は <strong style={{ color: 'var(--hold)' }}>{selectedDate}（{dateLabel}）</strong> 分として保存されます。書きかけの内容はそのままです。今日の日報を書くときは上の「今日」を押してください。</span>
          </div>
        )}
        {restoredDraft && (
          <div style={{
            marginTop: 10, padding: '10px 12px',
            background: 'var(--mark-soft)',
            border: '1px solid #33406E',
            borderRadius: 8, fontSize: 'var(--t-note)', color: 'var(--mark)', fontWeight: 600, lineHeight: 1.5,
            display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8,
          }}>
            <span style={{ display: 'flex', alignItems: 'flex-start', gap: 6 }}>
              <Save size={15} style={{ flexShrink: 0, marginTop: 1 }} />
              書きかけの内容を復元しました。まだ保存されていません。忘れずに「日報を保存する」を押してください。
            </span>
            <button onClick={discardDraft} style={{
              flexShrink: 0, background: 'transparent', border: '1px solid var(--rule-strong)',
              color: 'var(--mark)', borderRadius: 8, padding: '4px 10px', fontSize: 'var(--t-note)', fontWeight: 700, cursor: 'pointer',
            }}>破棄</button>
          </div>
        )}
        {existed && (
          <div style={{
            marginTop: 10, padding: '8px 12px',
            background: 'var(--gain-bg)',
            border: '1px solid #1E5540',
            borderRadius: 8, fontSize: 'var(--t-note)', color: 'var(--gain)', fontWeight: 600,
            display: 'flex', alignItems: 'center', gap: 6,
          }}>
            <CheckCircle size={14} /> この日のデータは既に保存されています（編集可）
          </div>
        )}
        {loadingDate && (
          <div style={{ marginTop: 10, fontSize: 'var(--t-note)', color: 'var(--ink-mute)', textAlign: 'center' }}>読み込み中...</div>
        )}
      </div>

      {/* 当日の着座メモサマリー */}
      {dayStats.total > 0 && (
        <div style={{ ...card, marginBottom: 16 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--ink-soft)', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 6 }}>
            <AlertTriangle size={14} color="var(--hold)" />
            {isToday ? '本日' : selectedDate} の着座メモ
          </div>

          {/* 結果サマリー */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 8, marginBottom: 16 }}>
            {[
              { label: '着座',   value: dayStats.total,    color: 'var(--mark)' },
              { label: '獲得',   value: dayStats.acq,      color: 'var(--gain)' },
              { label: '失注',   value: dayStats.lost,     color: 'var(--loss)' },
              { label: '後追い', value: dayStats.followup, color: 'var(--mark)' },
            ].map(({ label: lbl, value, color }) => (
              <div key={lbl} style={{ background: 'var(--paper-sunk)', borderRadius: 10, padding: '10px 8px', textAlign: 'center', border: `1px solid color-mix(in srgb, ${color} 13%, transparent)` }}>
                <div style={{ fontSize: 'var(--t-note)', color: 'var(--ink-mute)', marginBottom: 3 }}>{lbl}</div>
                <div style={{ fontSize: 20, fontWeight: 800, color }}>{value}</div>
              </div>
            ))}
          </div>

          {/* 失注理由ランキング */}
          {dayStats.lostRanking.length > 0 && (
            <>
              <div style={{ fontSize: 'var(--t-note)', fontWeight: 700, color: 'var(--ink-mute)', marginBottom: 8 }}>失注理由の内訳</div>
              {dayStats.lostRanking.map((r, i) => {
                const maxCnt = dayStats.lostRanking[0].cnt;
                const pct = maxCnt > 0 ? (r.cnt / maxCnt) * 100 : 0;
                const totalLost = dayStats.lostRanking.reduce((s, x) => s + x.cnt, 0);
                const ratePct = totalLost > 0 ? ((r.cnt / totalLost) * 100).toFixed(0) : 0;
                const color = i === 0 ? 'var(--loss)' : i === 1 ? 'var(--hold)' : 'var(--hold)';
                return (
                  <div key={r.reason} style={{ marginBottom: 10 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span style={{
                          fontSize: 'var(--t-note)', fontWeight: 800, color,
                          background: `color-mix(in srgb, ${color} 13%, transparent)`, borderRadius: 5,
                          padding: '1px 6px', minWidth: 20, textAlign: 'center',
                        }}>#{i + 1}</span>
                        <span style={{ fontSize: 'var(--t-note)', color: 'var(--ink)', fontWeight: 600 }}>{r.reason}</span>
                      </div>
                      <span style={{ fontSize: 'var(--t-note)', fontWeight: 700, color }}>
                        {r.cnt}件 <span style={{ fontSize: 'var(--t-note)', color: 'var(--ink-mute)' }}>({ratePct}%)</span>
                      </span>
                    </div>
                    <div style={{ height: 5, background: 'var(--paper-sunk)', borderRadius: 99 }}>
                      <div style={{ height: '100%', width: `${pct}%`, background: color, borderRadius: 99, transition: 'width 400ms' }} />
                    </div>
                  </div>
                );
              })}
            </>
          )}
        </div>
      )}

      {/* 当日の合計（時間別入力から自動集計・読み取り専用） */}
      <Section title={
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          {isToday ? '本日' : selectedDate} の合計（時間別入力より自動集計）
        </span>
      }>
        {summary ? (
          <>
            {/* 6項目を1行の帯にする。タイルを積むと「読むだけ」の部分が画面を占めてしまう。 */}
            <div className="sumstrip">
              {SUMMARY_METRICS.map(m => (
                <div key={m.key}>
                  <div className="lbl">{m.label}</div>
                  <div className="v num">{summary[m.key] || 0}</div>
                </div>
              ))}
            </div>

            <button
              type="button"
              onClick={() => setShowSummaryRatios(v => !v)}
              className="btn-secondary"
              style={{ marginTop: 'var(--s3)' }}
            >
              転換率を{showSummaryRatios ? '閉じる' : '見る'}
            </button>

            {showSummaryRatios && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(120px,1fr))', gap: 'var(--s3)', marginTop: 'var(--s3)' }}>
                {RATIOS.map(r => (
                  <div key={r.label} style={{ borderLeft: '2px solid var(--rule)', paddingLeft: 'var(--s2)' }}>
                    <div style={{ fontSize: 'var(--t-note)', color: 'var(--ink-soft)', whiteSpace: 'nowrap' }}>{r.label}</div>
                    <div className="num" style={{ fontSize: 'var(--t-lead)', fontWeight: 700, color: 'var(--ink)' }}>{pct(summary[r.num] || 0, summary[r.den] || 0)}</div>
                    {r.den === 'valid_seat' && invalidSeatCount(summary) > 0 && (
                <div style={{ fontSize: 'var(--t-note)', color: 'var(--ink-mute)' }} title={`「有効じゃない」${invalidSeatCount(summary)}件を分母から除いています`}>有効着座 {acqDenominator(summary)}</div>
              )}
                  </div>
                ))}
              </div>
            )}
            <div style={{ fontSize: 'var(--t-note)', color: 'var(--ink-soft)', marginTop: 'var(--s3)', lineHeight: 1.6 }}>
              数値の修正は <strong style={{ color: 'var(--mark)' }}>時間別入力</strong> から行ってください。着座・獲得は着座メモの時刻から自動反映されます。
            </div>
          </>
        ) : (
          <div style={{ fontSize: 'var(--t-note)', color: 'var(--ink-mute)', textAlign: 'center', padding: 12 }}>読み込み中...</div>
        )}
      </Section>

      {/* ブース・ブース代 */}
      <Section title="ブース / ブース代">
        <div style={{ marginBottom: 16 }}>
          <label style={label}>その日のブース</label>
          <div style={{ padding: '10px 14px', background: 'var(--paper-sunk)', border: '1px solid var(--rule-strong)', borderRadius: 10, fontSize: 14, fontWeight: 700, color: boothName ? 'var(--mark)' : 'var(--ink-mute)' }}>
            {boothName || '未設定（時間別入力で選択）'}
          </div>
        </div>
        <div>
          <label style={label}>
            自分のブース代（数字だけ）
            <span style={{ color: 'var(--loss)', marginLeft: 4 }}>＊必須</span>
          </label>
          <input
            ref={boothCostRef}
            type="number" inputMode="numeric" min="0"
            style={{ ...inp, ...(boothCostError ? { border: '1.5px solid #6B3A36', background: 'var(--loss-bg)' } : {}) }}
            value={form.booth_cost}
            onChange={e => { if (boothCostError) setBoothCostError(false); set('booth_cost', e.target.value); }}
            placeholder="例: 5000（無料なら 0）"
          />
          {boothCostError ? (
            <div style={{ fontSize: 'var(--t-note)', color: 'var(--loss)', marginTop: 6, fontWeight: 600 }}>
              ブース代を入力してください（無料の場合は 0 と入力）。
            </div>
          ) : (
            <div style={{ fontSize: 'var(--t-note)', color: 'var(--ink-soft)', marginTop: 6 }}>その日の出店料を入力。日報履歴に表示されます。</div>
          )}
        </div>
      </Section>

      {/* その日の時間別メモ（読み取り専用）。振り返りを書くときの材料になるよう、すぐ上に置く。 */}
      {hourMemos.length > 0 && (
        <Section title="この日の時間別メモ">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--s3)' }}>
            {hourMemos.map(({ hour, memo }) => (
              <div key={hour} style={{ display: 'flex', gap: 'var(--s3)', alignItems: 'baseline' }}>
                <span className="num" style={{
                  flexShrink: 0, minWidth: 52, fontSize: 'var(--t-note)',
                  fontWeight: 600, color: 'var(--mark)',
                }}>{hour}:00</span>
                <span style={{ color: 'var(--ink)', lineHeight: 1.7 }}>{memo}</span>
              </div>
            ))}
          </div>
          <div style={{ fontSize: 'var(--t-note)', color: 'var(--ink-mute)', marginTop: 'var(--s3)', lineHeight: 1.6 }}>
            直すときは <strong style={{ color: 'var(--mark)' }}>時間別入力</strong> から。
          </div>
        </Section>
      )}

      {/* 本日の振り返り（4項目） */}
      <Section title={
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          本日の振り返り
          <HelpTooltip title={HELP.reflection_why.title} body={HELP.reflection_why.body} />
        </span>
      }>
        <div style={{ marginBottom: 16 }}>
          <label style={{ ...label, color: 'var(--gain)' }}>今日良かったこと</label>
          <textarea
            style={{ ...inp, minHeight: 84, resize: 'vertical' }}
            value={form.next_pitch_text}
            onChange={e => set('next_pitch_text', e.target.value)}
            placeholder="今日うまくいった行動と、その理由"
          />
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={{ ...label, color: 'var(--loss)' }}>今日の課題</label>
          <textarea
            style={{ ...inp, minHeight: 84, resize: 'vertical' }}
            value={form.why_text}
            onChange={e => set('why_text', e.target.value)}
            placeholder="KPI基準・転換率を見て下がっている部分と、その原因"
          />
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={{ ...label, color: 'var(--mark)' }}>明日改善すること</label>
          <textarea
            style={{ ...inp, minHeight: 84, resize: 'vertical' }}
            value={form.improvement_text}
            onChange={e => set('improvement_text', e.target.value)}
            placeholder="明日、現場で変える具体的な行動"
          />
        </div>

        <div>
          <label style={{ ...label, color: 'var(--hold)' }}>困っていること</label>
          <textarea
            style={{ ...inp, minHeight: 84, resize: 'vertical' }}
            value={form.prevent_stage}
            onChange={e => set('prevent_stage', e.target.value)}
            placeholder="管理者に確認・相談したいこと"
          />
        </div>
      </Section>

      {/* 今日一緒に入ったメンバーへのフィードバック（ADR 0021）。
          保存は下の「日報を保存する」とは独立させている（日報保存の失敗や画面遷移で
          書いた内容が消えるのを防ぐため。docs/bugs/2026-07-13-shiftend-draft-lost-on-navigation.md）。 */}
      <Section title="今日一緒に入ったメンバーへ">
        <PeerFeedbackComposer date={selectedDate} />
      </Section>

      {/* 保存 */}
      <button onClick={handleSave} disabled={saving} style={{
        width: '100%', padding: '16px', borderRadius: 16,
        background: saved ? 'var(--gain-solid)' : 'var(--grad-primary)',
        border: 'none', color: '#fff', fontWeight: 700, fontSize: 16, cursor: 'pointer',
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
        boxShadow: '0 4px 20px var(--mark)', transition: 'all 200ms',
        opacity: saving ? 0.7 : 1,
      }}>
        {saved ? <><CheckCircle size={18} /> 保存しました！</> : <><Save size={18} /> {saving ? '保存中...' : '日報を保存する'}</>}
      </button>
    </div>
  );
}
